import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Switch,
  Typography,
} from '@mui/material';
import { PillButton, sa } from '../../superAdminUi';
import ModalShell from './ModalShell';
import RoleMultiSelect from '../RoleMultiSelect';
import { inviteUserWithAccess, searchRoles } from '../../../services/api/identityUserService';
import adminProvisioningService from '../../../services/api/adminProvisioningService';
import schoolStaffService from '../../../services/api/schoolStaffService';
import {
  buildInvitationData,
  buildScopeAssignments,
  buildProvisioningData,
} from '../userFields';
import { buildRoleOptions, SAFE_SPACE_ROLE_CODES } from '../roleCatalog';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALL_SCHOOLS = { id: 0, name: 'All Schools' };

const emptyForm = { firstName: '', lastName: '', email: '' };

const labelSx = {
  fontFamily: sa.font.family,
  fontSize: 13,
  fontWeight: sa.font.semibold,
  color: sa.color.text,
  mb: 0.5,
  display: 'block',
};
const fieldSx = {
  fontFamily: sa.font.family,
  '& .MuiOutlinedInput-root': { borderRadius: sa.radius.sm, fontFamily: sa.font.family },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};

// Add User — mirrors the People Management access-management flow: enter the person's
// details, choose the sites (group scope), assign one or more roles sourced from Identity,
// optionally require MFA, then provision. Provisioning is two atomic steps — the Identity
// invite (creates the user, roles + scopes, returns a userId) followed by the Admin-Api
// provisioning that wires that user into the dashboards — with a staff-role link for the
// roles that need one (Owner in a single school, SafeSpace roles).
function AddUserModal({ open, onClose, scope, schoolId, groupId, parentGroupId, scopeName, siteOptions, onCompleted }) {
  const [form, setForm] = useState(emptyForm);
  const [sites, setSites] = useState([]); // group scope: array of {id,name}
  const [roleCodes, setRoleCodes] = useState([]);
  const [enableMfa, setEnableMfa] = useState(false);
  const [phase, setPhase] = useState('form'); // form | submitting | success
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [identityRoles, setIdentityRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const isGroup = scope === 'group';

  // "All schools" when nothing is chosen, or the explicit All Schools option is picked.
  const selectedSiteIds = useMemo(() => {
    if (!isGroup) return [Number(schoolId)];
    if (sites.length === 0 || sites.some((s) => s.id === 0)) return [];
    return sites.map((s) => s.id);
  }, [isGroup, sites, schoolId]);
  const allSiteIds = useMemo(() => (siteOptions || []).map((s) => s.id), [siteOptions]);

  // SafeSpace roles are only meaningful for a single school (school scope, or a group
  // narrowed to exactly one school).
  const singleSchool = !isGroup || selectedSiteIds.length === 1;

  const roleOptions = useMemo(
    () => buildRoleOptions(identityRoles, { singleSchool }),
    [identityRoles, singleSchool]
  );

  const reset = () => {
    setForm(emptyForm);
    setSites([]);
    setRoleCodes([]);
    setEnableMfa(false);
    setPhase('form');
    setError('');
    setFieldErrors({});
  };

  // Load the Identity role catalogue when the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRolesLoading(true);
    searchRoles()
      .then((roles) => {
        if (!cancelled) setIdentityRoles(Array.isArray(roles) ? roles : []);
      })
      .catch(() => {
        if (!cancelled) setIdentityRoles([]); // fall back to catalogue names
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Drop any selected role that is no longer offered (e.g. a SafeSpace role after the site
  // selection widens past a single school).
  useEffect(() => {
    const available = new Set(roleOptions.map((o) => o.code));
    setRoleCodes((prev) => {
      const next = prev.filter((code) => available.has(code));
      return next.length === prev.length ? prev : next;
    });
  }, [roleOptions]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const setField = (key) => (e) => {
    // Capture the value eagerly — reading e.target.value inside the updater would read it
    // after the controlled input has reconciled back to state.
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Enter a first name.';
    if (!form.lastName.trim()) errs.lastName = 'Enter a last name.';
    if (!EMAIL_PATTERN.test(form.email.trim())) errs.email = 'Enter a valid email address.';
    if (roleCodes.length === 0) errs.roles = 'Choose at least one role.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const selectedOptions = roleOptions.filter((o) => roleCodes.includes(o.code));
  const roleIds = selectedOptions.map((o) => o.id).filter((id) => id !== undefined && id !== null);

  // The staff-role link runs only for roles that need a school-staff record, and only when
  // the grant targets a single school.
  const targetSchoolId = !isGroup
    ? Number(schoolId)
    : selectedSiteIds.length === 1
      ? selectedSiteIds[0]
      : null;

  const maybeLinkStaffRoles = async (userId) => {
    const hasSafeSpace = roleCodes.some((c) => SAFE_SPACE_ROLE_CODES.includes(c));
    const hasOwner = roleCodes.includes('Owner');
    if (!targetSchoolId || (!hasSafeSpace && !(hasOwner && singleSchool))) return;

    const rolesToSend = [
      ...roleCodes.filter((c) => SAFE_SPACE_ROLE_CODES.includes(c)),
      ...(hasOwner && singleSchool ? ['Owner'] : []),
    ];
    if (rolesToSend.length === 0) return;

    // Create a backing staff record for the link (no staff-detail form is shown; the
    // record is the minimum the link needs).
    const staffResponse = await schoolStaffService.addSchoolStaff({
      FirstName: form.firstName.trim(),
      LastName: form.lastName.trim(),
      Email: form.email.trim(),
      SchoolId: targetSchoolId,
      StartDate: '',
      EmploymentType: 1,
      CoreFilters: [{ JobTitle: 'Teaching' }],
      FilterValues: [],
    });
    const staffId = staffResponse?.data?.Id ?? staffResponse?.Id ?? staffResponse?.id;
    if (!staffId) throw new Error('Could not create the staff record for role assignment.');

    await adminProvisioningService.upsertUserStaffRoles({
      staffId,
      schoolId: targetSchoolId,
      userId,
      roles: rolesToSend,
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setPhase('submitting');
    setError('');

    let userId;
    try {
      // Step 1 — Identity invite (atomic: user + roles + scopes, seat-checked).
      const invitePayload = {
        ...buildInvitationData({
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          enableMfa,
        }),
        roleCodes,
        scopeAssignments: buildScopeAssignments({
          scope,
          schoolId,
          groupId,
          siteIds: selectedSiteIds,
          allSiteIds,
          parentGroupId,
        }),
      };
      const inviteResult = await inviteUserWithAccess(invitePayload);
      userId = inviteResult?.userId ?? inviteResult?.data?.userId;
    } catch (err) {
      setError(err?.message || 'The user could not be invited. Please try again.');
      setPhase('form');
      return;
    }

    try {
      // Step 2 — Admin provisioning (wires the invited user into the dashboards).
      await adminProvisioningService.provisionUser(
        buildProvisioningData({
          userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          scope,
          groupId,
          schoolId,
          siteIds: selectedSiteIds,
          allSiteIds,
          roleIds,
        })
      );
      // Step 3 — staff-role link for the roles that need one.
      await maybeLinkStaffRoles(userId);
      setPhase('success');
      if (onCompleted) onCompleted();
    } catch (err) {
      // The user exists in Identity; provisioning is what failed. Surface it plainly.
      setError(
        (err?.message || 'The user was invited but could not be fully provisioned.') +
          ' You may need to retry provisioning.'
      );
      setPhase('form');
    }
  };

  const submitting = phase === 'submitting';

  return (
    <ModalShell open={open} onClose={handleClose} title='Add User' width={{ xs: '95%', sm: 560 }}>
      {phase === 'success' ? (
        <Box>
          <Alert severity='success' sx={{ mb: 2, fontFamily: sa.font.family }}>
            {form.firstName || 'The user'} has been created and provisioned. They will receive an
            email to set their password.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <PillButton type='button' $variant='outline' $size='lg' onClick={reset}>
              Add another
            </PillButton>
            <PillButton type='button' $variant='teal' $size='lg' onClick={handleClose}>
              Done
            </PillButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {error && <Alert severity='error' sx={{ fontFamily: sa.font.family }}>{error}</Alert>}

          <Box>
            <Typography sx={{ fontFamily: sa.font.family, fontSize: 13, color: sa.color.textGray, }}>
              Adding a user to 
              <span style={{ fontFamily: sa.font.family, fontSize: 15, fontWeight: sa.font.bold, color: sa.color.text, marginLeft: '4px' }}>
                {scopeName || (isGroup ? 'This group' : 'This school')}
              </span>
            </Typography>
            
          </Box>

          {isGroup && (
            <Box>
              <Typography component='label' sx={labelSx}>
                Sites
              </Typography>
              <Autocomplete
                multiple
                options={[ALL_SCHOOLS, ...(siteOptions || [])]}
                value={sites}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_e, next) => {
                  // Choosing "All Schools" clears specific picks, and vice-versa.
                  const pickedAll = next.some((s) => s.id === 0) && !sites.some((s) => s.id === 0);
                  setSites(pickedAll ? [ALL_SCHOOLS] : next.filter((s) => s.id !== 0));
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((opt, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={opt.id}
                      label={opt.name}
                      size='small'
                      sx={{ fontFamily: sa.font.family, bgcolor: sa.color.schoolBg, color: sa.color.text }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={sites.length ? '' : 'All schools'}
                    helperText='Leave empty to grant access across all schools in the group.'
                    sx={fieldSx}
                  />
                )}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography component='label' sx={labelSx}>First Name</Typography>
              <TextField
                value={form.firstName}
                onChange={setField('firstName')}
                fullWidth
                error={Boolean(fieldErrors.firstName)}
                helperText={fieldErrors.firstName}
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography component='label' sx={labelSx}>Last Name</Typography>
              <TextField
                value={form.lastName}
                onChange={setField('lastName')}
                fullWidth
                error={Boolean(fieldErrors.lastName)}
                helperText={fieldErrors.lastName}
                sx={fieldSx}
              />
            </Box>
          </Box>

          <Box>
            <Typography component='label' sx={labelSx}>Email</Typography>
            <TextField
              type='email'
              value={form.email}
              onChange={setField('email')}
              fullWidth
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
              sx={fieldSx}
            />
          </Box>

          <RoleMultiSelect
            options={roleOptions}
            value={roleCodes}
            onChange={setRoleCodes}
            loading={rolesLoading}
            error={Boolean(fieldErrors.roles)}
            helperText={fieldErrors.roles}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              border: `1px solid ${sa.color.stroke}`,
              borderRadius: sa.radius.sm,
            }}
          >
            <Box>
              <Typography sx={{ fontFamily: sa.font.family, fontSize: 14, fontWeight: sa.font.semibold, color: sa.color.text }}>
                Require Multi-Factor Authentication
              </Typography>
              <Typography sx={{ fontFamily: sa.font.family, fontSize: 12.5, color: sa.color.textGray }}>
                The user must set up MFA when they first sign in.
              </Typography>
            </Box>
            <Switch
              checked={enableMfa}
              onChange={(e) => setEnableMfa(e.target.checked)}
              sx={{ '& .Mui-checked': { color: sa.color.teal }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: sa.color.teal } }}
              inputProps={{ 'aria-label': 'Require Multi-Factor Authentication' }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <PillButton type='button' $variant='outline' $size='lg' onClick={handleClose} disabled={submitting}>
              Cancel
            </PillButton>
            <PillButton type='button' $variant='teal' $size='lg' onClick={handleSubmit} disabled={submitting}>
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Add User'}
            </PillButton>
          </Box>
        </Box>
      )}
    </ModalShell>
  );
}

AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scopeName: PropTypes.string,
  siteOptions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
  onCompleted: PropTypes.func,
};

AddUserModal.defaultProps = {
  siteOptions: [],
};

export default AddUserModal;
